import React, { useState, useRef, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Page } from '../../types';

// グローバル状態管理
const globalSlideStates: Record<string, {
  currentSlideIndex: number;
  offset: number;
  containerWidth: number;
}> = {};

export const getGlobalSlideState = (pageId: string) => {
  return globalSlideStates[pageId] || {
    currentSlideIndex: 0,
    offset: 0,
    containerWidth: 0
  };
};

const setGlobalSlideState = (pageId: string, state: Partial<{
  currentSlideIndex: number;
  offset: number;
  containerWidth: number;
}>) => {
  globalSlideStates[pageId] = {
    ...getGlobalSlideState(pageId),
    ...state
  };
};

// リセットが必要かどうかを判定する関数
const shouldResetSlideState = (pageId: string, initialIndex: number = 1) => {
  const state = getGlobalSlideState(pageId);
  const targetIndex = initialIndex - 1;
  // 既に初期表示位置にいる場合はリセットしない（ちらつき防止）
  return state.currentSlideIndex !== targetIndex;
};

// スライド状態をリセットする関数
const resetSlideState = (pageId: string, initialIndex: number = 1) => {
  if (shouldResetSlideState(pageId, initialIndex)) {
    setGlobalSlideState(pageId, {
      currentSlideIndex: initialIndex - 1,
      offset: initialIndex - 1
      // containerWidthは保持
    });
  }
};

interface PartialHorizontalSlideProps {
  slides: Partial<Page>[];
  className?: string;
  slideWidth?: number; // スライド（画像）の幅（パーセンテージ、デフォルト: 100）
  viewportWidth?: number; // 表示領域の幅（パーセンテージ、デフォルト: slideWidthと同じ）※画像80% + 左右余白10%ずつ = 表示領域100%などの場合に使用
  indicatorPosition?: 'top' | 'bottom'; // ドット位置（デフォルト: 'bottom'）
  infiniteLoop?: boolean; // 無限ループ設定（デフォルト: true）
  initialIndex?: number; // 初期表示スライド（1始まり）
  positionAdjustment?: number; // 位置調整（px）
  indicatorOffsetX?: number; // ナビゲーションドット左右位置調整（px、デフォルト: 0）
  indicatorOffsetY?: number; // ナビゲーションドット上下位置調整（px、デフォルト: 0）
  pageId?: string; // ページID（状態保持用）
  onPageChange?: () => void; // ページ変更時のコールバック
}

export const PartialHorizontalSlide: React.FC<PartialHorizontalSlideProps> = ({
  slides,
  className = '',
  slideWidth = 100,
  viewportWidth, // デフォルトはundefined（slideWidthと同じ扱い）
  indicatorPosition = 'bottom',
  infiniteLoop = true,
  initialIndex = 1,
  positionAdjustment = 0,
  indicatorOffsetX = 0,
  indicatorOffsetY = 0,
  pageId,
  onPageChange
}) => {
  // viewportWidthが指定されていない場合はslideWidthを使用
  const effectiveViewportWidth = viewportWidth ?? slideWidth;
  // 有効なスライドのみをフィルタリング
  const validSlides = slides.filter((slide): slide is Partial<Page> & { backgroundImage: string } => 
    slide && slide.backgroundImage !== undefined
  );
  
  const totalSlides = validSlides.length;
  
  // 無限ループ設定に応じてスライドを準備
  const displaySlides = totalSlides > 0 && infiniteLoop ? [
    ...validSlides,
    ...validSlides,
    ...validSlides,
    ...validSlides
  ] : validSlides;

  // グローバル状態を使用（pageIdがある場合のみ）
  const globalState = pageId ? getGlobalSlideState(pageId) : null;
  
  // 初期インデックスを計算（0始まり）
  const calculatedInitialIndex = Math.max(0, Math.min((initialIndex - 1) || 0, totalSlides - 1));
  
  const [internalCurrentSlideIndex, setInternalCurrentSlideIndex] = useState(
    globalState?.currentSlideIndex ?? calculatedInitialIndex
  );
  const [internalOffset, setInternalOffset] = useState(
    globalState?.offset ?? calculatedInitialIndex
  );
  const [internalContainerWidth, setInternalContainerWidth] = useState(
    globalState?.containerWidth ?? 0
  );
  
  const currentSlideIndex = globalState?.currentSlideIndex ?? internalCurrentSlideIndex;
  const offset = globalState?.offset ?? internalOffset;
  const containerWidth = globalState?.containerWidth ?? internalContainerWidth;
  
  
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef(currentSlideIndex);
  const prevPageIdRef = useRef(pageId);
  
  // 最新の状態を追跡するRef
  const currentSlideIndexRef = useRef(currentSlideIndex);
  useEffect(() => {
    currentSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex]);
  
  // 初期表示スライドの適用（初回のみ）
  const initialIndexApplied = useRef(false);
  useEffect(() => {
    if (totalSlides > 0 && !initialIndexApplied.current && pageId) {
      const targetIndex = Math.max(0, Math.min(initialIndex - 1, totalSlides - 1));
      // グローバル状態が初期値と異なる場合のみ更新
      const currentState = getGlobalSlideState(pageId);
      if (currentState.currentSlideIndex !== targetIndex) {
        setCurrentSlideIndex(targetIndex);
      }
      initialIndexApplied.current = true;
    }
  }, [totalSlides, initialIndex, pageId]);

  // ページ変更時のリセット処理
  useEffect(() => {
    if (pageId && pageId !== prevPageIdRef.current) {
      if (onPageChange) {
        onPageChange();
      }
      prevPageIdRef.current = pageId;
    }
  }, [pageId, onPageChange]);
  
  // グローバルリセットイベントのリスナー
  useEffect(() => {
    const handleReset = (event: CustomEvent) => {
      const targetPageId = event.detail?.pageId;
      if (targetPageId && pageId && targetPageId === pageId) {
        resetSlideState(pageId, initialIndex);
      }
    };
    
    window.addEventListener('partialSlideReset', handleReset as EventListener);
    
    return () => {
      window.removeEventListener('partialSlideReset', handleReset as EventListener);
    };
  }, [pageId, initialIndex]);
  
  // 次のスライドに進むイベントのリスナー（デモモード用）
  useEffect(() => {
    const handleNext = (event: CustomEvent) => {
      const targetPageId = event.detail?.pageId;
      console.log('[PartialHorizontalSlide] Event received:', { targetPageId, pageId, match: targetPageId === pageId });
      
      if (targetPageId && pageId && targetPageId === pageId) {
        // 現在のインデックスをRefから取得（最新の値）
        const current = currentSlideIndexRef.current;
        console.log('[PartialHorizontalSlide] Current index from ref:', current);
        
        const nextIndex = infiniteLoop 
          ? current + 1 
          : Math.min(current + 1, totalSlides - 1);
        
        console.log('[PartialHorizontalSlide] Moving to index:', nextIndex, 'totalSlides:', totalSlides);
        
        // グローバル状態とローカル状態の両方を更新
        if (pageId) {
          setGlobalSlideState(pageId, { currentSlideIndex: nextIndex });
        }
        setInternalCurrentSlideIndex(nextIndex);
        currentSlideIndexRef.current = nextIndex;
      }
    };
    
    window.addEventListener('partialSlideNext', handleNext as EventListener);
    
    return () => {
      window.removeEventListener('partialSlideNext', handleNext as EventListener);
    };
  }, [pageId, infiniteLoop, totalSlides]);
  
  // 状態変更ハンドラー
  const setCurrentSlideIndex = (index: number | ((prev: number) => number)) => {
    const indexValue = typeof index === 'function' ? index(currentSlideIndex) : index;
    if (pageId) {
      setGlobalSlideState(pageId, { currentSlideIndex: indexValue });
    }
    setInternalCurrentSlideIndex(indexValue);
  };
  
  const setOffset = (newOffset: number | ((prev: number) => number)) => {
    const offsetValue = typeof newOffset === 'function' ? newOffset(offset) : newOffset;
    if (pageId) {
      setGlobalSlideState(pageId, { offset: offsetValue });
    }
    setInternalOffset(offsetValue);
  };

  // コンテナの幅を取得
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        if (pageId) {
          setGlobalSlideState(pageId, { containerWidth: width });
        }
        setInternalContainerWidth(width);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    return () => window.removeEventListener('resize', updateWidth);
  }, [pageId]);
  
  // スライド処理（無限ループ設定に応じて制御）
  useEffect(() => {
    if (totalSlides === 0) return;
    
    const diff = currentSlideIndex - prevIndexRef.current;
    if (diff === 0) return;
    
    prevIndexRef.current = currentSlideIndex;
    
    if (infiniteLoop) {
      // 無限ループ時：差分を反映してスライド後、位置をリセット
      setIsTransitioning(true);
      setOffset(prev => prev + diff);
      
      const timer = setTimeout(() => {
        const normalizedIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;
        requestAnimationFrame(() => {
          setIsTransitioning(false);
          requestAnimationFrame(() => {
            setOffset(normalizedIndex);
          });
        });
      }, 850);
      
      return () => clearTimeout(timer);
    } else {
      // 無限ループ無効時：直接位置を設定
      setIsTransitioning(true);
      setOffset(currentSlideIndex);
      
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 850);
      
      return () => clearTimeout(timer);
    }
  }, [currentSlideIndex, totalSlides, infiniteLoop]);

  // スワイプ処理（無限ループ設定に応じて制御）
  const goToNext = () => {
    if (infiniteLoop) {
      setCurrentSlideIndex(prev => prev + 1);
    } else {
      setCurrentSlideIndex(prev => Math.min(prev + 1, totalSlides - 1));
    }
  };

  const goToPrevious = () => {
    if (infiniteLoop) {
      setCurrentSlideIndex(prev => prev - 1);
    } else {
      setCurrentSlideIndex(prev => Math.max(prev - 1, 0));
    }
  };

  // タッチ・マウスイベントでスワイプ処理
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
  const [endPos, setEndPos] = useState<{ x: number; y: number } | null>(null);

  const minSwipeDistance = 50;

  const handleStart = (clientX: number, clientY: number) => {
    setEndPos(null);
    setStartPos({ x: clientX, y: clientY });
  };

  const handleMove = (clientX: number, clientY: number) => {
    setEndPos({ x: clientX, y: clientY });
  };

  const handleEnd = () => {
    if (!startPos || !endPos) return;
    
    const distanceX = startPos.x - endPos.x;
    const distanceY = startPos.y - endPos.y;
    const isLeftSwipe = distanceX > minSwipeDistance;
    const isRightSwipe = distanceX < -minSwipeDistance;
    const isVerticalSwipe = Math.abs(distanceY) > Math.abs(distanceX);

    if (isLeftSwipe && !isVerticalSwipe) {
      goToNext();
    }
    if (isRightSwipe && !isVerticalSwipe) {
      goToPrevious();
    }
  };

  // タッチイベント
  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    handleStart(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    handleMove(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    handleEnd();
  };

  // マウスイベント
  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (startPos) {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    }
  };

  const onMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    handleEnd();
  };

  if (validSlides.length === 0) {
    return (
      <div className="partial-horizontal-slide-wrapper">
        <div className={`partial-horizontal-slide-container empty ${className}`}>
          <div className="empty-slide">
            <div className="empty-placeholder">
              <span>スライドが設定されていません</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (validSlides.length === 1) {
    const hasViewportWidth = viewportWidth !== undefined;
    const itemWidth = hasViewportWidth ? effectiveViewportWidth : slideWidth;
    const imageWidth = hasViewportWidth ? slideWidth : 100;
    const horizontalPadding = hasViewportWidth ? ((effectiveViewportWidth - slideWidth) / 2) : 0;
    
    return (
      <div className="partial-horizontal-slide-wrapper">
        <div 
          ref={containerRef}
          className={`partial-horizontal-slide-container single ${className}`}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <div style={{ 
            width: `${itemWidth}%`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingLeft: hasViewportWidth ? `${horizontalPadding}%` : 0,
            paddingRight: hasViewportWidth ? `${horizontalPadding}%` : 0,
            boxSizing: 'border-box',
          }}>
            <img
              src={validSlides[0].backgroundImage}
              alt="Slide 1"
              className="partial-slide-image"
              style={{
                width: hasViewportWidth ? `${(imageWidth / itemWidth) * 100}%` : '100%',
                height: 'auto',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // 表示用のインデックス（0〜totalSlides-1）
  const displayIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;

  return (
    <div 
      className={`partial-horizontal-slide-wrapper indicator-${indicatorPosition}`}
      style={{
        paddingTop: positionAdjustment > 0 ? `${positionAdjustment}px` : 0,
        paddingBottom: positionAdjustment < 0 ? `${Math.abs(positionAdjustment)}px` : 0
      }}
    >
      <div 
        ref={containerRef}
        className={`partial-horizontal-slide-container ${className}`}
        style={{
          touchAction: 'none',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <div
          className="partial-slides-wrapper"
          style={{
            transform: containerWidth > 0 
              ? (() => {
                  // viewportWidthが設定されている場合は、中央配置の補正は不要
                  const centeringOffset = viewportWidth !== undefined ? 0 : containerWidth * ((100 - effectiveViewportWidth) / 200);
                  const slideOffset = containerWidth * (effectiveViewportWidth / 100);
                  
                  if (infiniteLoop) {
                    return `translateX(${-(offset + totalSlides) * slideOffset + centeringOffset}px)`;
                  } else {
                    return `translateX(${-offset * slideOffset + centeringOffset}px)`;
                  }
                })()
              : 'none',
            transition: isTransitioning ? 'transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {displaySlides.map((slide, index) => {
            // viewportWidthが設定されている場合、スライドアイテムの幅はviewportWidth、画像の幅はslideWidthで制御
            const hasViewportWidth = viewportWidth !== undefined;
            const itemWidth = hasViewportWidth ? effectiveViewportWidth : slideWidth;
            // 画像の幅は、itemWidthに対するslideWidthの割合で計算
            const imageWidthPercent = hasViewportWidth ? (slideWidth / effectiveViewportWidth) * 100 : 100;
            // 画像幅が100%未満の場合のみ余白を追加
            const horizontalPadding = slideWidth < 100 ? 16 : 0;
            
            return (
              <div
                key={`slide-${slide.id || index}-${index}`}
                className="partial-slide-item"
                style={{
                  width: containerWidth > 0 ? `${containerWidth * (itemWidth / 100)}px` : `${itemWidth}%`,
                  minWidth: containerWidth > 0 ? `${containerWidth * (itemWidth / 100)}px` : `${itemWidth}%`,
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingLeft: `${horizontalPadding}px`,
                  paddingRight: `${horizontalPadding}px`,
                  boxSizing: 'border-box',
                }}
              >
                <img
                  src={slide.backgroundImage}
                  alt={`Slide ${index + 1}`}
                  className="partial-slide-image"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  style={{
                    width: `${imageWidthPercent}%`,
                    height: 'auto',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </div>
            );
          })}
        </div>

      </div>

      {/* ナビゲーションドット（画像の上） */}
      {validSlides.length > 1 && indicatorPosition === 'top' && (
        <div 
          className="partial-slide-navigation partial-slide-navigation-top"
          style={{
            transform: `translate(calc(-50% + ${indicatorOffsetX}px), calc(-100% - 16px + ${indicatorOffsetY}px))`
          }}
        >
          {validSlides.map((_, index) => {
            const displayIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;
            return (
              <button
                key={index}
                className={`partial-nav-dot ${index === displayIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentSlideIndex(index);
                }}
                aria-label={`スライド ${index + 1} に移動`}
              />
            );
          })}
        </div>
      )}

      {/* ナビゲーションドット（画像の下） */}
      {validSlides.length > 1 && indicatorPosition === 'bottom' && (
        <div 
          className="partial-slide-navigation partial-slide-navigation-bottom"
          style={{
            transform: `translate(calc(-50% + ${indicatorOffsetX}px), calc(100% + 16px + ${indicatorOffsetY}px))`
          }}
        >
          {validSlides.map((_, index) => {
            const displayIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;
            return (
              <button
                key={index}
                className={`partial-nav-dot ${index === displayIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentSlideIndex(index);
                }}
                aria-label={`スライド ${index + 1} に移動`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
